import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Plus, Trash2, Calendar, Tag, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DndContext, DragEndEvent, DragOverlay, DragStartEvent, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from '@dnd-kit/core';
import CreateOrderDialog, { phaseLabels } from '@/components/admin/CreateOrderDialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { exportToCsv } from '@/lib/csvExport';
import type { Database } from '@/integrations/supabase/types';

type OrderPhase = Database['public']['Enums']['order_phase'];
type Order = Database['public']['Tables']['orders']['Row'];

const COLUMNS: OrderPhase[] = ['inquiry', 'design', 'airbrush', 'clear_coat', 'done'];

const phaseColor: Record<OrderPhase, string> = {
  inquiry: 'border-l-muted-foreground',
  design: 'border-l-blue-500',
  airbrush: 'border-l-primary',
  clear_coat: 'border-l-purple-500',
  done: 'border-l-emerald-500',
  cancelled: 'border-l-destructive',
};

const OrderCard = ({ order, onDelete }: { order: Order; onDelete: (id: string) => void }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: order.id });
  const isLate = order.deadline && new Date(order.deadline) < new Date() && order.phase !== 'done';
  const deadlineSoon = order.deadline && !isLate &&
    (new Date(order.deadline).getTime() - Date.now()) < 7 * 24 * 60 * 60 * 1000;

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={`p-3 bg-card border border-border border-l-4 ${phaseColor[order.phase]} rounded-md cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-30' : ''}`}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-foreground truncate">{order.client_name}</p>
          {order.team && <p className="text-xs text-muted-foreground truncate flex items-center gap-1"><Tag size={11} />{order.team}</p>}
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button
              onPointerDown={(e) => e.stopPropagation()}
              className="text-muted-foreground hover:text-destructive shrink-0"
            >
              <Trash2 size={14} />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Smazat zakázku?</AlertDialogTitle>
              <AlertDialogDescription>{order.client_name}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Zrušit</AlertDialogCancel>
              <AlertDialogAction onClick={() => onDelete(order.id)} className="bg-destructive text-destructive-foreground">Smazat</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        {order.deadline ? (
          <span className={`flex items-center gap-1 ${isLate ? 'text-destructive font-medium' : deadlineSoon ? 'text-amber-500' : 'text-muted-foreground'}`}>
            <Calendar size={11} />
            {new Date(order.deadline).toLocaleDateString('cs-CZ')}
          </span>
        ) : <span />}
        {order.price && <span className="text-primary font-medium">{Number(order.price).toLocaleString('cs-CZ')} Kč</span>}
      </div>
    </div>
  );
};

const Column = ({ phase, orders, onDelete }: { phase: OrderPhase; orders: Order[]; onDelete: (id: string) => void }) => {
  const { setNodeRef, isOver } = useDroppable({ id: phase });
  return (
    <div
      ref={setNodeRef}
      className={`flex-1 min-w-[240px] p-3 bg-secondary/30 rounded-md border ${isOver ? 'border-primary' : 'border-transparent'} transition-colors`}
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider">{phaseLabels[phase]}</h3>
        <span className="text-xs text-muted-foreground bg-card px-2 py-0.5 rounded-full">{orders.length}</span>
      </div>
      <div className="space-y-2 min-h-[100px]">
        {orders.map((o) => <OrderCard key={o.id} order={o} onDelete={onDelete} />)}
      </div>
    </div>
  );
};

const OrdersAdmin = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showCancelled, setShowCancelled] = useState(false);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const fetch = useCallback(async () => {
    const { data } = await supabase.from('orders').select('*').order('created_at', { ascending: false });
    setOrders(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const handleDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));

  const handleDragEnd = async (e: DragEndEvent) => {
    setActiveId(null);
    const orderId = String(e.active.id);
    const newPhase = e.over?.id as OrderPhase | undefined;
    if (!newPhase) return;
    const order = orders.find((o) => o.id === orderId);
    if (!order || order.phase === newPhase) return;

    setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, phase: newPhase } : o)));
    const { error } = await supabase.from('orders').update({ phase: newPhase }).eq('id', orderId);
    if (error) {
      toast.error('Nepodařilo se přesunout');
      fetch();
    }
  };

  const deleteOrder = async (id: string) => {
    const { error } = await supabase.from('orders').delete().eq('id', id);
    if (error) { toast.error('Chyba'); return; }
    toast.success('Smazáno');
    fetch();
  };

  const exportCsv = () => {
    exportToCsv('zakazky.csv', orders, [
      { key: 'client_name', label: 'Klient' },
      { key: 'client_email', label: 'Email' },
      { key: 'client_phone', label: 'Telefon' },
      { key: 'team', label: 'Tým' },
      { key: 'phase', label: 'Fáze' },
      { key: 'deadline', label: 'Termín' },
      { key: 'price', label: 'Cena' },
      { key: 'notes', label: 'Poznámky' },
      { key: 'created_at', label: 'Vytvořeno' },
    ]);
  };

  const visible = showCancelled ? orders : orders.filter((o) => o.phase !== 'cancelled');
  const activeOrder = activeId ? orders.find((o) => o.id === activeId) : null;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-4xl tracking-wider text-foreground" style={{ fontFamily: 'Bebas Neue' }}>
          Zakázky
        </h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowCancelled((v) => !v)}>
            {showCancelled ? 'Skrýt zrušené' : 'Zobrazit zrušené'}
          </Button>
          <Button variant="outline" size="sm" onClick={exportCsv}>
            <Download size={14} className="mr-1" /> CSV
          </Button>
          <CreateOrderDialog
            onCreated={fetch}
            trigger={<Button size="sm"><Plus size={14} className="mr-1" /> Nová zakázka</Button>}
          />
        </div>
      </div>

      {loading ? (
        <p className="text-muted-foreground">Načítám…</p>
      ) : (
        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="flex gap-3 overflow-x-auto pb-4">
            {COLUMNS.map((phase) => (
              <Column
                key={phase}
                phase={phase}
                orders={visible.filter((o) => o.phase === phase)}
                onDelete={deleteOrder}
              />
            ))}
            {showCancelled && (
              <Column
                phase="cancelled"
                orders={visible.filter((o) => o.phase === 'cancelled')}
                onDelete={deleteOrder}
              />
            )}
          </div>
          <DragOverlay>
            {activeOrder && (
              <div className="p-3 bg-card border border-primary rounded-md shadow-2xl">
                <p className="font-medium">{activeOrder.client_name}</p>
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
};

export default OrdersAdmin;
